// Tracking plan specific types
export enum TrackingPlanType {
  Live = 'LIVE',
  RuleLibrary = 'RULE_LIBRARY',
  PropertyLibrary = 'PROPERTY_LIBRARY'
}

export const tpTypeToString = (tpType: TrackingPlanType): string => {
  switch (tpType) {
    case TrackingPlanType.Live:
      return 'Live'
    case TrackingPlanType.PropertyLibrary:
      return 'Property Library'
    case TrackingPlanType.RuleLibrary:
      return 'Event Library'
  }
}

export interface TrackingPlan {
  id: string
  name: string
  updatedAt: Date
  type: TrackingPlanType
  rules: Rule[]
  properties: Properties
  requiredProperties: string[]
  syncedConnections?: SyncedConnection[]
  syncedImports?: SyncedImport[]
  resourceId?: string
}

export interface Properties {
  [key: string]: Property
}

export interface LibraryPropertyId {
  key: string
  libraryId: string
}

export interface LibraryRuleId {
  key: string
  type: RuleType
  version: number
  libraryId: string
}

// Rule specific types
export interface RuleId {
  key: string
  version: number
  type: RuleType
}

export interface IVersionedRule {
  key: string
  type: RuleType
  versions: Rule[]
}

interface BasicRuleProps {
  key: string
  type: RuleType
  version: number
  jsonSchema: RuleJsonSchema
  id: string
}

export interface Rule extends BasicRuleProps {
  updatedAt?: string
  createdAt?: string
  draftRuleMetadata?: {
    createdBy: string
    isNew: boolean
  }
}

export interface DraftRule extends BasicRuleProps {
  operationType: OperationType
  createdBy: string
  isNew?: boolean
}

export interface FlattenedRule extends Rule {
  properties: Property[]
  traits: Property[]
  flatProperties: Property[]
  flatTraits: Property[]
  hasAdvancedKeywords?: boolean
}

export interface Property {
  depth: number
  descendants?: Property[]
  hasChildren: boolean
  id?: string
  key: string
  rules?: Property | JsonSchemaProperty
  status: string
  type: PropertyType[] | PropertyType
  description?: string
  parentType?: string
  properties?: { [key: string]: JsonSchemaProperty }
  required?: string[]
}

export enum EventType {
  Track = 'TRACK',
  Identify = 'IDENTIFY',
  Group = 'GROUP',
  Page = 'PAGE',
  Screen = 'SCREEN',
  Alias = 'ALIAS'
}

export enum RuleType {
  Common = 'COMMON',
  Track = 'TRACK',
  Identify = 'IDENTIFY',
  Group = 'GROUP'
}

type PropertyType = 'any' | 'null' | 'string' | 'array' | 'object' | 'boolean' | 'number' | 'integer'

export interface RuleJsonSchema {
  $schema?: string
  description?: string
  labels?: object
  required?: string[]
  properties?: {
    context?: JsonSchemaProperty
    traits?: JsonSchemaProperty
    properties?: JsonSchemaProperty
  }
  title?: string
  type?: string
}

interface JsonSchemaProperty {
  id?: string
  type?: string
  description?: string
  properties?: { [key: string]: JsonSchemaProperty }
  required?: string[]
}

// Operation specific types
export interface Operation {
  jsonSchema: any
  key: string
  operation: OperationType
  type: string
  version: number
}

export enum OperationType {
  JsonSchemaUpdated = 'JSON_SCHEMA_UPDATED',
  NameUpdated = 'NAME_UPDATED',
  PropertyAdded = 'PROPERTY_ADDED',
  PropertyConditionalUpdated = 'PROPERTY_CONDITIONAL_UPDATED',
  PropertyDeleted = 'PROPERTY_DELETED',
  PropertyDescriptionUpdated = 'PROPERTY_DESCRIPTION_UPDATED',
  PropertyNameUpdated = 'PROPERTY_NAME_UPDATED',
  PropertyStatusUpdated = 'PROPERTY_STATUS_UPDATED',
  PropertyTypeUpdated = 'PROPERTY_TYPE_UPDATED',
  RuleAdded = 'RULE_ADDED',
  RuleDeleted = 'RULE_DELETED',
  RuleDescriptionUpdated = 'RULE_DESCRIPTION_UPDATED',
  RuleLabelUpdated = 'RULE_LABEL_UPDATED',
  RuleTitleUpdated = 'RULE_TITLE_UPDATED',
  Unknown = 'OPERATION_UNKNOWN'
}

export interface SyncedConnection {
  id: string
  resourceSchemaId: string
  name: string
  type: TrackingPlanType
  description: string
  slug: string
  workspaceId: string
  updatedAt: Date
  createdAt: Date
}

export interface SyncedImport {
  libraryId: string
  libraryRuleJson: RuleJsonSchema
  libraryType: TrackingPlanType
  trackingPlanRuleKey: string
  trackingPlanId: string
  trackingPlanRuleType: RuleType
  trackingPlanRuleVersion: number
}
